import { Injectable } from '@angular/core';
import { Metadata, RunView } from '@memberjunction/core';
import { GraphQLActionClient, GraphQLDataProvider } from '@memberjunction/graphql-dataprovider';

/** Operations the server-side Assist Message Writing action accepts. */
export type WritingAssistOperation = 'write' | 'reword' | 'shorten' | 'expand' | 'professional' | 'friendly' | 'proofread' | 'custom';

/** One menu entry for the editor's AI button. */
export interface WritingAssistMenuItem {
  operation: WritingAssistOperation;
  label: string;
  /** Past-tense label for the inline result bar ("Shortened 1 paragraph"). */
  doneLabel: string;
  icon: string;
  /** Whether the operation needs a free-text instruction from the user. */
  needsInstruction: boolean;
  /** Placeholder for the instruction box. */
  instructionPrompt?: string;
  /** Whether the operation works without any text (Help me write can start from nothing). */
  allowsEmptyText: boolean;
}

export const WRITING_ASSIST_MENU: ReadonlyArray<WritingAssistMenuItem> = [
  { operation: 'write', label: 'Help me write', doneLabel: 'Wrote', icon: 'fa-solid fa-pen-nib', needsInstruction: true, instructionPrompt: 'What should it say?', allowsEmptyText: true },
  { operation: 'reword', label: 'Reword', doneLabel: 'Reworded', icon: 'fa-solid fa-shuffle', needsInstruction: false, allowsEmptyText: false },
  { operation: 'shorten', label: 'Shorten', doneLabel: 'Shortened', icon: 'fa-solid fa-compress', needsInstruction: false, allowsEmptyText: false },
  { operation: 'expand', label: 'Expand', doneLabel: 'Expanded', icon: 'fa-solid fa-expand', needsInstruction: false, allowsEmptyText: false },
  { operation: 'professional', label: 'More professional', doneLabel: 'Made more professional', icon: 'fa-solid fa-user-tie', needsInstruction: false, allowsEmptyText: false },
  { operation: 'friendly', label: 'More friendly', doneLabel: 'Made friendlier', icon: 'fa-regular fa-face-smile', needsInstruction: false, allowsEmptyText: false },
  { operation: 'proofread', label: 'Fix spelling & grammar', doneLabel: 'Fixed spelling & grammar', icon: 'fa-solid fa-spell-check', needsInstruction: false, allowsEmptyText: false },
  { operation: 'custom', label: 'Custom instruction', doneLabel: 'Applied your instruction', icon: 'fa-solid fa-wand-magic-sparkles', needsInstruction: true, instructionPrompt: 'How should it change?', allowsEmptyText: false },
];

/** Size caps mirrored from the server so the UI can refuse before a round trip. */
export const WRITING_ASSIST_MAX_TEXT_CHARS = 20_000;
export const WRITING_ASSIST_MAX_INSTRUCTION_CHARS = 500;
export const WRITING_ASSIST_MAX_THREAD_CONTEXT_CHARS = 8_000;

export interface WritingAssistRequest {
  operation: WritingAssistOperation;
  /** The text to work on (HTML from the editor). May be empty only for `write`. */
  text: string;
  /** Required for write / custom. */
  instruction?: string;
  /** The channel message being edited (preferred: proves org scope on the server). */
  channelMessageId?: string | null;
  /** The channel a new message is composed on (when no message exists yet). */
  channelId?: string | null;
  /** Plain-text excerpt of the message being replied to (write only). */
  threadContext?: string | null;
  format?: 'html' | 'text';
}

export interface WritingAssistResponse {
  success: boolean;
  text?: string;
  model?: string;
  /** Server result code (NO_AI_KEY, FEATURE_DISABLED, INPUT_TOO_LONG, MODEL_ERROR, BAD_OUTPUT, NOT_FOUND…). */
  resultCode?: string;
  /** User-facing failure message. */
  error?: string;
}

/**
 * Thin client over the server-side "Assist Message Writing" action: the AI button in the
 * message editor. The browser sends the unsaved text plus the message (or channel) it belongs
 * to; the server resolves the channel as the signed-in user, applies the organization's voice
 * and AI keys, and returns a suggestion. Nothing is cached: every click is a fresh suggestion.
 */
@Injectable({ providedIn: 'root' })
export class IzzyWritingAssistService {
  private static readonly ACTION_NAME = 'Assist Message Writing';

  private actionIdLookup: Promise<string | null> | null = null;

  public async assist(request: WritingAssistRequest): Promise<WritingAssistResponse> {
    const text = request.text ?? '';
    const instruction = (request.instruction ?? '').trim();
    if (text.length > WRITING_ASSIST_MAX_TEXT_CHARS) {
      return { success: false, resultCode: 'INPUT_TOO_LONG', error: `That's too much text for the assistant (limit ${WRITING_ASSIST_MAX_TEXT_CHARS.toLocaleString()} characters). Select a smaller part.` };
    }
    if (instruction.length > WRITING_ASSIST_MAX_INSTRUCTION_CHARS) {
      return { success: false, resultCode: 'INPUT_TOO_LONG', error: `Keep the instruction under ${WRITING_ASSIST_MAX_INSTRUCTION_CHARS} characters.` };
    }
    if (!request.channelMessageId && !request.channelId) {
      return { success: false, resultCode: 'MISSING_PARAMETERS', error: 'The assistant needs to know which channel this message is for.' };
    }

    const actionId = await this.getActionId();
    if (!actionId) {
      return { success: false, resultCode: 'NOT_INSTALLED', error: 'The writing assistant is not installed on this server.' };
    }

    const params: Array<{ Name: string; Value: string; Type: 'Input' }> = [
      { Name: 'Operation', Value: request.operation, Type: 'Input' },
      { Name: 'Text', Value: text, Type: 'Input' },
      { Name: 'Format', Value: request.format ?? 'html', Type: 'Input' },
    ];
    if (instruction) params.push({ Name: 'Instruction', Value: instruction, Type: 'Input' });
    if (request.channelMessageId) params.push({ Name: 'ChannelMessageID', Value: request.channelMessageId, Type: 'Input' });
    else if (request.channelId) params.push({ Name: 'ChannelID', Value: request.channelId, Type: 'Input' });
    const threadContext = (request.threadContext ?? '').slice(0, WRITING_ASSIST_MAX_THREAD_CONTEXT_CHARS);
    if (request.operation === 'write' && threadContext.trim()) params.push({ Name: 'ThreadContext', Value: threadContext, Type: 'Input' });

    try {
      const client = new GraphQLActionClient(Metadata.Provider as GraphQLDataProvider);
      const result = await client.RunAction(actionId, params as never);
      if (!result?.Success) {
        const code = (result as { ResultCode?: string } | undefined)?.ResultCode;
        return { success: false, resultCode: code, error: this.friendlyError(code, result?.Message) };
      }
      const outputs = this.readOutputs(result.Result);
      const suggestion = outputs.get('SuggestedText');
      if (!suggestion) {
        return { success: false, resultCode: 'BAD_OUTPUT', error: 'The assistant returned no text. Try again.' };
      }
      return { success: true, text: suggestion, model: outputs.get('Model') || undefined, resultCode: 'SUCCESS' };
    } catch (error) {
      return { success: false, resultCode: 'REQUEST_FAILED', error: error instanceof Error ? error.message : 'The assistant could not be reached.' };
    }
  }

  /** Plain words for the codes staff are likely to hit; the server message otherwise. */
  private friendlyError(code: string | undefined, serverMessage: string | undefined): string {
    switch (code) {
      case 'NO_AI_KEY':
      case 'NO_AI_CONFIGURATION':
        return 'No usable AI key for this organization. Check AI credentials in Admin.';
      case 'FEATURE_DISABLED':
        return 'The writing assistant is turned off for this organization.';
      case 'INPUT_TOO_LONG':
        return serverMessage || 'That is too much text for the assistant. Select a smaller part.';
      case 'NOT_FOUND':
        return 'This message is not available to you.';
      case 'MODEL_ERROR':
      case 'BAD_OUTPUT':
        return "Couldn't get a suggestion. Try again.";
      default:
        return serverMessage || "Couldn't get a suggestion. Try again.";
    }
  }

  /** Resolves (once) the action's ID by name; a null result is not cached so a later install is picked up. */
  private getActionId(): Promise<string | null> {
    if (!this.actionIdLookup) {
      this.actionIdLookup = (async () => {
        try {
          const rv = new RunView();
          const res = await rv.RunView<{ ID: string }>({
            EntityName: 'MJ: Actions',
            ExtraFilter: `Name = '${IzzyWritingAssistService.ACTION_NAME}'`,
            Fields: ['ID'],
            MaxRows: 1,
          });
          const id = (res.Success ? res.Results : [])[0]?.ID ?? null;
          if (!id) {
            this.actionIdLookup = null;
          }
          return id;
        } catch {
          this.actionIdLookup = null;
          return null;
        }
      })();
    }
    return this.actionIdLookup;
  }

  /**
   * ActionResult types Result as the result-code entity, but the GraphQL client actually parses
   * the action's Output params into it. MJServer runs that array through CopyScalarsAndArrays,
   * which can turn it into an index-keyed OBJECT ({"0": {...}}), so accept both shapes.
   */
  private readOutputs(raw: unknown): Map<string, string> {
    const candidates: unknown[] = Array.isArray(raw)
      ? raw
      : raw && typeof raw === 'object' ? Object.values(raw) : [];
    const outputs = new Map<string, string>();
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') continue;
      const { Name, Value } = candidate as { Name?: unknown; Value?: unknown };
      if (typeof Name === 'string' && typeof Value === 'string') {
        outputs.set(Name, Value);
      }
    }
    return outputs;
  }
}
